import {
    InputOperator,
    Operator,
    OperatorTypeEnum,
    Workflow,
} from "@/model/Workflow";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Switch} from "@/components/ui/switch";
import _ from "lodash";
import React, {useEffect, useMemo, useState} from "react";
import PythonOperatorForm from "@/view/workflow/components/operatorForm/PythonOperatorForm";
import PythonBranchOperatorForm from "@/view/workflow/components/operatorForm/PythonBranchOperatorForm";
import LLMOperatorForm from "@/view/workflow/components/operatorForm/LLMOperatorForm";
import InputOperatorForm from "@/view/workflow/components/operatorForm/InputOperatorForm";
import SearchRAGOperatorForm from "@/view/workflow/components/operatorForm/SearchRAGOperatorForm";
import SearchFAQOperatorForm from "@/view/workflow/components/operatorForm/SearchFAQOperatorForm";
import LangChainOperatorForm from "@/view/workflow/components/operatorForm/LangChainOperatorForm";


const hasGeneralFields = (operator: Operator | InputOperator): operator is Operator => {
    return operator.type !== "input";
}

interface OperatorFormProps {
    workflow: Workflow;
    operator: Operator | InputOperator;
    readOnly?: boolean;
    syncWorkflow?: () => void;
}

const OperatorForm: React.FC<OperatorFormProps> = (props) => {
    const {workflow, operator, readOnly, syncWorkflow} = props;

    const debouncedNameChange = useMemo(
        () =>
            _.debounce((name: string) => {
                if (!hasGeneralFields(operator))
                    return;

                operator.name = name;
                syncWorkflow && syncWorkflow();
            }, 300),
        [operator, syncWorkflow],
    );

    return (
        <div className="flex h-full flex-col gap-4 p-1">
            {hasGeneralFields(operator) && (
                <div className="rounded-lg border bg-muted/30 p-4 flex flex-col gap-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        General
                    </p>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor={`operator-name-${operator._id}`}>Name</Label>
                        <Input
                            id={`operator-name-${operator._id}`}
                            key={operator._id}
                            placeholder="Operator name"
                            defaultValue={operator.name}
                            disabled={readOnly}
                            onChange={(e) => debouncedNameChange(e.target.value)}
                        />
                    </div>
                    <RecursionLimitField operator={operator} readOnly={readOnly} />
                </div>
            )}

            <div className="min-h-0 flex-1">
                <OperatorDetailForm workflow={workflow}
                                    operator={operator}
                                    readOnly={readOnly}
                                    syncWorkflow={syncWorkflow}
                />
            </div>
        </div>
    );
};

const isRecursionEnabled = (recursionLimit: number | null | undefined): boolean => {
    return recursionLimit != null;
}

type RecursionLimitFieldProps = {
    operator: Operator;
    readOnly?: boolean;
};

function RecursionLimitField({operator, readOnly}: RecursionLimitFieldProps) {
    const [enabled, setEnabled] = useState(() => isRecursionEnabled(operator.recursionLimit));
    const [inputValue, setInputValue] = useState(() =>
        isRecursionEnabled(operator.recursionLimit) ? String(operator.recursionLimit) : "1",
    );

    useEffect(() => {
        setEnabled(isRecursionEnabled(operator.recursionLimit));
        setInputValue(
            isRecursionEnabled(operator.recursionLimit) ? String(operator.recursionLimit) : "1",
        );
    }, [operator._id, operator.recursionLimit]);

    const handleSwitchChange = (checked: boolean) => {
        setEnabled(checked);
        if (checked) {
            const next = Math.max(1, operator.recursionLimit ?? 1);
            operator.recursionLimit = next;
            setInputValue(String(next));
            return;
        }
        operator.recursionLimit = null;
        setInputValue("1");
    };

    const handleInputChange = (value: string) => {
        setInputValue(value);
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            return;
        }
        operator.recursionLimit = Math.max(1, Math.floor(parsed));
    };

    const handleInputBlur = () => {
        if (!enabled) {
            return;
        }
        const next = Math.max(1, operator.recursionLimit ?? 1);
        operator.recursionLimit = next;
        setInputValue(String(next));
    };

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`operator-recursion-${operator._id}`}>Recursion Limit</Label>
                <Switch
                    id={`operator-recursion-switch-${operator._id}`}
                    checked={enabled}
                    onCheckedChange={handleSwitchChange}
                    disabled={readOnly}
                />
            </div>
            <Input
                id={`operator-recursion-${operator._id}`}
                type="number"
                min={1}
                placeholder="1"
                value={enabled ? inputValue : ""}
                disabled={readOnly || !enabled}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={handleInputBlur}
            />
        </div>
    );
}

interface OperatorDetailFormProps {
    workflow: Workflow;
    operator: Operator | InputOperator;
    readOnly?: boolean;
    syncWorkflow?: () => void;
}

const OperatorDetailForm: React.FC<OperatorDetailFormProps> = (props) => {
    const {workflow, operator, readOnly, syncWorkflow} = props;

    switch (operator.type) {
        case OperatorTypeEnum.PYTHON:
            return <PythonOperatorForm operator={operator} readOnly={readOnly} />;
        case OperatorTypeEnum.BRANCH_PYTHON:
            return <PythonBranchOperatorForm operator={operator} readOnly={readOnly} />;
        case OperatorTypeEnum.LLM:
            return (
                <LLMOperatorForm operator={operator}
                                 readOnly={readOnly}
                                 syncWorkflow={syncWorkflow}
                />
            );
        case OperatorTypeEnum.LANGCHAIN:
            return (
                <LangChainOperatorForm operator={operator}
                                       readOnly={readOnly}
                                       syncWorkflow={syncWorkflow}
                />
            );
        case "input":
            return (
                <InputOperatorForm
                    workflow={workflow}
                    operator={operator}
                    readOnly={readOnly}
                />
            );
        case OperatorTypeEnum.SEARCH_DATA_SOURCE_RAG:
            return <SearchRAGOperatorForm operator={operator} readOnly={readOnly} />;
        case OperatorTypeEnum.SEARCH_DATA_SOURCE_FAQ:
            return <SearchFAQOperatorForm operator={operator} readOnly={readOnly} />;
        default:
            return <div className="flex h-full items-center justify-center">...</div>;
    }
}

export default OperatorForm;
